<script setup lang="ts">
/**
 * Filter drawer for the withdraw work queue.
 *
 * Slides in from the right; same fields as the (now-deleted) inline
 * FILTERS card — status (constrained to actionable), date range,
 * investor name, investor email. Pinned "Apply filters" CTA at the
 * bottom per the canonical drawer-chrome pattern in
 * `.claude/rules/ui-kit-theme.md → Drawer chrome for form drawers`.
 *
 * Filter values are owned as a draft inside this component until the
 * operator hits Apply. The header "Reset" link clears every field at
 * once and emits `reset`.
 */
import { reactive, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import Input from '@uiKit/elements/Input.vue'
import Select from '@uiKit/elements/Select.vue'
import DatePicker from '@uiKit/elements/DatePicker.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import FormField from '@uiKit/components/FormField.vue'
import { WITHDRAW_REQUEST_STATUS, type WithdrawRequestStatus } from '../types/withdrawRequestsTypes'

export interface WithdrawRequestsFilterValues {
  status: WithdrawRequestStatus | null
  fromDate: Date | null
  toDate: Date | null
  investorName: string
  investorEmail: string
}

interface Props {
  visible: boolean
  values: WithdrawRequestsFilterValues
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'apply', values: WithdrawRequestsFilterValues): void
  (e: 'reset'): void
}>()

const draft = reactive<WithdrawRequestsFilterValues>({
  status: props.values.status,
  fromDate: props.values.fromDate,
  toDate: props.values.toDate,
  investorName: props.values.investorName,
  investorEmail: props.values.investorEmail,
})

// Re-sync the draft whenever the drawer is reopened so the operator
// always starts from the currently-applied values, not stale draft
// values from a previous open.
watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) return
    draft.status = props.values.status
    draft.fromDate = props.values.fromDate
    draft.toDate = props.values.toDate
    draft.investorName = props.values.investorName
    draft.investorEmail = props.values.investorEmail
  },
)

// Status select is constrained to the work-queue's actionable statuses.
// "All actionable" maps to null at the parent's listParams.
const statusOptions = [
  { label: 'All actionable', value: null },
  { label: 'Pending approval', value: WITHDRAW_REQUEST_STATUS.PENDING_APPROVAL },
  { label: 'Awaiting settlement', value: WITHDRAW_REQUEST_STATUS.AWAITING_SETTLEMENT },
]

function handleApply() {
  emit('apply', {
    status: draft.status,
    fromDate: draft.fromDate,
    toDate: draft.toDate,
    investorName: draft.investorName.trim(),
    investorEmail: draft.investorEmail.trim(),
  })
  emit('update:visible', false)
}

function handleReset() {
  draft.status = null
  draft.fromDate = null
  draft.toDate = null
  draft.investorName = ''
  draft.investorEmail = ''
  emit('reset')
}

function handleClose() {
  emit('update:visible', false)
}
</script>

<template>
  <AppDrawer
    :model-value="visible"
    position="right"
    width="440px"
    :show-close-icon="false"
    @update:model-value="(v) => !v && handleClose()"
  >
    <template #header>
      <DrawerHeader title="Filter requests" subtitle="Refine the queue" @close="handleClose">
        <template #extras>
          <Button label="Reset" variant="text" color="secondary" @click="handleReset" />
        </template>
      </DrawerHeader>
    </template>

    <Box flex direction="col" class="drawer-body">
      <Box flex direction="col" gap="4" class="drawer-body__content">
        <FormField label="Status">
          <Select v-model="draft.status" :options="statusOptions" placeholder="All actionable" />
        </FormField>
        <FormField label="From">
          <DatePicker v-model="draft.fromDate" placeholder="DD/MM/YYYY" date-format="dd/mm/yy" />
        </FormField>
        <FormField label="To">
          <DatePicker v-model="draft.toDate" placeholder="DD/MM/YYYY" date-format="dd/mm/yy" />
        </FormField>
        <FormField label="Investor name">
          <Input v-model="draft.investorName" placeholder="Name contains…" />
        </FormField>
        <FormField label="Investor email">
          <Input v-model="draft.investorEmail" placeholder="Email contains…" />
        </FormField>
      </Box>

      <Box class="drawer-body__actions">
        <Button label="Apply filters" color="primary" block size="large" @click="handleApply" />
      </Box>
    </Box>
  </AppDrawer>
</template>

<style scoped>
.drawer-body {
  min-height: 100%;
  padding: 1.25rem 1.5rem;
}

.drawer-body__content {
  flex: 1;
}

.drawer-body__actions {
  margin-top: auto;
  padding-top: 1rem;
}

@media (max-width: 992px) {
  .drawer-body {
    padding: 1rem;
  }
}
</style>
